import { gatewayApi as api } from './gatewayApi';

// Types
export interface WorkOrder {
  id?: string;
  wo_num: string;           // Database column: wo_num
  qty: number;              // Database column: qty
  po_id: string;            // Database column: po_id
  wo_status_id: number;     // Database column: wo_status_id
  is_rework: boolean;       // Database column: is_rework
  is_active?: boolean;      // Database column: is_active
  created_date?: string;    // Database column: created_date
  modified_date?: string;   // Database column: modified_date
  obsolete_date?: string;   // Database column: obsolete_date
  last_operation_id?: string; // Database column: last_operation_id
  doc_id?: string;          // Database column: doc_id
  
  // Related entities
  purchase_order?: {
    id: string;
    po_number?: string;
    client_id?: string;
    client?: {
      id: string;
      name: string;
      description?: string;
    };
  };
  
  route_card?: {
    id: string;
    name: string;
    description?: string;
    operations?: WorkOrderOperation[];
  };
  
  status?: {
    id: number;
    name: string;
    description?: string;
  };
  
  part?: {
    id: string;
    part_num?: string;
    description?: string;
  };
  
  client?: {
    id: string;
    name: string;
    description?: string;
  };
  
  creator?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  
  Serials?: WorkOrderSerial[];
  OperationLogs?: WorkOrderOperationLog[];
}

export interface WorkOrderSerial {
  id?: string;
  WOId: string;
  SerialNum: string;
  PartNumber: string;
  PartDescription: string;
  Active?: boolean;
}

export interface WorkOrderOperation {
  id?: string;
  RCTId: string;
  OperationId: string;
  Sequence: number;
  EstimatedTime: number;
  Active?: boolean;
  
  // Related entities
  Operation?: {
    id: string;
    Name: string;
    Description: string;
    OperationType?: {
      id: string;
      Name: string;
    };
  };
  
  Inputs?: WorkOrderOperationInput[];
  WorkInstructions?: WorkInstruction[];
  ToolFixtures?: ToolFixture[];
}

export interface WorkOrderOperationInput {
  id?: string;
  RCTDetailId: string;
  InputId: string;
  Required: boolean;
  Active?: boolean;
  
  // Related entities
  Input?: {
    id: string;
    Name: string;
    InputType?: {
      id: string;
      Name: string;
    };
  };
}

export interface WorkOrderOperationLog {
  id?: string;
  WOKey: string;
  OperatorKey: string;
  RCTKey: string;
  OperationKey: string;
  SerialNumber: string;
  StartTime: string;
  EndTime?: string;
  Status: 'IN_PROGRESS' | 'PASSED' | 'FAILED' | 'PAUSED';
  Active?: boolean;
  
  // Related entities
  WorkOrder?: WorkOrder;
  Operator?: {
    id: string;
    Name: string;
    Email: string;
  };
  RouteCard?: {
    id: string;
    Name: string;
  };
  Operation?: {
    id: string;
    Name: string;
  };
}

export interface WorkInstruction {
  id?: string;
  Name: string;
  Description: string;
  FilePath?: string;
  Active?: boolean;
}

export interface ToolFixture {
  id?: string;
  Name: string;
  SerialNumber: string;
  ToolTypeId: string;
  Active?: boolean;
  
  // Related entities
  ToolType?: {
    id: string;
    Name: string;
  };
}

export interface WorkOrderStatus {
  id: number;
  Name: string;
  Color: string;
  Active?: boolean;
}

export interface RouteCard {
  id: string;
  Name: string;
  Description: string;
  PartId: string;
  Active?: boolean;
  
  // Related entities
  Part?: {
    id: string;
    Number: string;
    Description: string;
  };
  
  Operations?: WorkOrderOperation[];
}

export interface CreateWorkOrderRequest {
  POId: string;
  routeCardData: {
    RCTId: string;
  };
  woSerialData: WorkOrderSerial[];
}

export interface SearchWorkOrdersRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    IsRework?: boolean;
    WOStatusId?: number;
    POId?: string;
    [key: string]: any;
  };
}

export interface SearchWorkOrdersResponse {
  data: WorkOrder[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

export interface OperationLogRequest {
  WOKey: string;
  OperatorKey: string;
  RCTKey: string;
  OperationKey: string;
  SerialNumber: string;
  Status: 'IN_PROGRESS' | 'PASSED' | 'FAILED' | 'PAUSED';
}

// Transform frontend data to backend format
const transformToBackendFormat = (wo: WorkOrder) => ({
  POId: parseInt(wo.po_id),
  IsRework: wo.is_rework || false,
  routeCardData: wo.route_card ? [{ RCTId: parseInt(wo.route_card.id) }] : [],
  woSerialData: wo.Serials?.map(serial => ({
    PartPOId: parseInt(serial.id || '0'),
    SerialNum: serial.SerialNum
  })) || []
});

// Transform backend data to frontend format
const transformFromBackendFormat = (backendWO: any): WorkOrder => ({
  id: backendWO.Id?.toString(),
  wo_num: backendWO.WONum,
  qty: backendWO.Qty,
  po_id: backendWO.POId?.toString(),
  wo_status_id: backendWO.WOStatusId,
  is_rework: backendWO.IsRework,
  is_active: backendWO.Active,
  created_date: backendWO.CreatedDate,
  modified_date: backendWO.ModifiedDate,
  obsolete_date: backendWO.ObsoleteDate,
  last_operation_id: backendWO.LastOperationId?.toString(),
  doc_id: backendWO.DocId?.toString(),
  purchase_order: backendWO.PurchaseOrder ? {
    id: backendWO.PurchaseOrder.Id?.toString(),
    po_number: backendWO.PurchaseOrder.Number,
    client_id: backendWO.PurchaseOrder.ClientId?.toString(),
    client: backendWO.PurchaseOrder.Client ? {
      id: backendWO.PurchaseOrder.Client.Id?.toString(),
      name: backendWO.PurchaseOrder.Client.Name,
      description: backendWO.PurchaseOrder.Client.Description
    } : undefined
  } : undefined,
  route_card: backendWO.RouteCards?.[0] ? {
    id: backendWO.RouteCards[0].Id?.toString(),
    name: backendWO.RouteCards[0].Name,
    description: backendWO.RouteCards[0].Description,
    operations: backendWO.RouteCards[0].Operations?.map((op: any) => ({
      id: op.Id?.toString(),
      RCTId: backendWO.RouteCards[0].Id?.toString(),
      OperationId: op.Id?.toString(),
      Sequence: op.Sequence || 0,
      EstimatedTime: op.EstimatedTime || 0,
      Active: op.Active,
      Operation: op.Operation ? {
        id: op.Operation.Id?.toString(),
        Name: op.Operation.Name,
        Description: op.Operation.Description,
        OperationType: op.Operation.OperationType ? {
          id: op.Operation.OperationType.Id?.toString(),
          Name: op.Operation.OperationType.Name
        } : undefined
      } : undefined,
      Inputs: op.Inputs?.map((input: any) => ({
        id: input.Id?.toString(),
        RCTDetailId: backendWO.RouteCards[0].Id?.toString(),
        InputId: input.Id?.toString(),
        Required: input.Required || false,
        Active: input.Active,
        InputValue: input.InputValue || '',
        Input: input.Input ? {
          id: input.Input.Id?.toString(),
          Name: input.Input.Name,
          InputType: input.Input.InputType ? {
            id: input.Input.InputType.Id?.toString(),
            Name: input.Input.InputType.Name
          } : undefined
        } : undefined
      })) || [],
      WorkInstructions: op.WorkInstructions?.map((wi: any) => ({
        id: wi.Id?.toString(),
        Name: wi.Name,
        Description: wi.Description,
        FilePath: wi.FilePath,
        Active: wi.Active
      })) || [],
      ToolFixtures: op.ToolFixtures?.map((tf: any) => ({
        id: tf.Id?.toString(),
        Name: tf.Name,
        SerialNumber: tf.SerialNumber,
        ToolTypeId: tf.ToolTypeId?.toString(),
        Active: tf.Active,
        ToolType: tf.ToolType ? {
          id: tf.ToolType.Id?.toString(),
          Name: tf.ToolType.Name
        } : undefined
      })) || []
    })) || []
  } : undefined,
  status: backendWO.Status ? {
    id: backendWO.Status.Id,
    name: backendWO.Status.Name,
    description: backendWO.Status.Description
  } : undefined,
  Serials: backendWO.Serials?.map((serial: any) => ({
    id: serial.Id?.toString(),
    WOId: backendWO.Id?.toString(),
    SerialNum: serial.SerialNum,
    PartNumber: serial.PartNumber || '',
    PartDescription: serial.PartDescription || '',
    Active: serial.Active
  })) || [],
  OperationLogs: backendWO.OperationLogs || []
});

// Work Order API Service
export const workOrderApi = {
  // Get all work orders with optional filtering
  getAll: (params?: SearchWorkOrdersRequest): Promise<SearchWorkOrdersResponse> =>
    api.post<SearchWorkOrdersResponse>('/workorder/search', params).then(response => ({
      data: response.data?.map(transformFromBackendFormat) || [],
      totalCount: response.totalCount || 0,
      pageNum: response.pageNum || 1,
      pageSize: response.pageSize || 20
    })),

  // Get work order by ID
  getById: (id: string): Promise<WorkOrder> =>
    api.get<{ data: any }>(`/workorder/${id}`).then(response => transformFromBackendFormat(response.data)),

  // Create new work order
  create: (data: CreateWorkOrderRequest): Promise<WorkOrder> =>
    api.post<{ data: any }>('/workorder/', { data: transformToBackendFormat(data as any) }).then(response => transformFromBackendFormat(response.data)),

  // Update work order
  update: (data: Partial<WorkOrder>): Promise<WorkOrder> =>
    api.put<{ data: any }>('/workorder/', { 
      id: parseInt(data.id!), 
      data: transformToBackendFormat(data as WorkOrder) 
    }).then(response => transformFromBackendFormat(response.data)),

  // Delete work order
  delete: (id: string): Promise<void> =>
    api.delete<void>(`/workorder/${id}`),

  // Get work orders by Purchase Order IDs
  getByPOIds: (poIds: string[]): Promise<WorkOrder[]> =>
    api.post('/workorder/search', { filter: { POId: poIds } }),

  // Get work orders by status
  getByStatus: (statusName: string): Promise<WorkOrder[]> =>
    api.get(`/workorder/search/WOStatus.Name/${statusName}`),

  // Work Order Serials
  getSerials: (workOrderId: string): Promise<WorkOrderSerial[]> =>
    api.get(`/workorder/${workOrderId}/serial`),

  addSerial: (workOrderId: string, serial: Omit<WorkOrderSerial, 'id' | 'WOId'>): Promise<WorkOrderSerial> =>
    api.post(`/workorder/${workOrderId}/serial`, serial),

  updateSerial: (serialData: WorkOrderSerial): Promise<WorkOrderSerial> =>
    api.put('/workorder/serial', serialData),

  deleteSerial: (serialId: string): Promise<void> =>
    api.delete(`/workorder/serial/${serialId}`),

  // Operation Logs
  getOperationLogs: (workOrderId: string): Promise<WorkOrderOperationLog[]> =>
    api.get(`/wooperationlog/search`, { filter: { WOKey: workOrderId } }),

  createOperationLog: (logData: OperationLogRequest): Promise<WorkOrderOperationLog> =>
    api.post('/wooperationlog/', logData),

  updateOperationLog: (logData: Partial<WorkOrderOperationLog>): Promise<WorkOrderOperationLog> =>
    api.put('/wooperationlog/', logData),

  // Operation Inputs
  getOperationInputs: (operationId: string): Promise<WorkOrderOperationInput[]> =>
    api.get(`/wooperationinput/search`, { filter: { RCTDetailId: operationId } }),

  createOperationInput: (inputData: Omit<WorkOrderOperationInput, 'id'>): Promise<WorkOrderOperationInput> =>
    api.post('/wooperationinput/', inputData),

  updateOperationInput: (inputData: WorkOrderOperationInput): Promise<WorkOrderOperationInput> =>
    api.put('/wooperationinput/', inputData),

  // Route Cards
  getRouteCards: (): Promise<RouteCard[]> =>
    api.get('/routecard/'),

  getRouteCardById: (id: string): Promise<RouteCard> =>
    api.get(`/routecard/${id}`),

  getRouteCardsByPart: (partId: string): Promise<RouteCard[]> =>
    api.get(`/routecard/search`, { filter: { PartId: partId } }),

  // Work Order Statuses
  getWorkOrderStatuses: (): Promise<WorkOrderStatus[]> =>
    api.get('/status/'),

  // Work Instructions
  getWorkInstructions: (operationId: string): Promise<WorkInstruction[]> =>
    api.get(`/workinstruction/search`, { filter: { OperationId: operationId } }),

  // Tool Fixtures
  getToolFixtures: (operationId: string): Promise<ToolFixture[]> =>
    api.get(`/toolfixture/search`, { filter: { OperationId: operationId } }),

  // Utility methods
  approveSerial: (workOrderId: string, serialNum: string): Promise<void> =>
    api.put('/workorder/approve', { id: workOrderId, serialNum }),

  rejectSerial: (workOrderId: string, serialNum: string, reason?: string): Promise<void> =>
    api.put('/workorder/reject', { id: workOrderId, serialNum, reason }),

  closeWorkOrder: (workOrderId: string): Promise<WorkOrder> =>
    api.put('/workorder/', { id: workOrderId, WOStatusId: 3 }), // Assuming 3 is "Closed"

  reopenWorkOrder: (workOrderId: string): Promise<WorkOrder> =>
    api.put('/workorder/', { id: workOrderId, WOStatusId: 1 }), // Assuming 1 is "Open"
}; 